import { inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanActivateFn,
  CanMatch,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
} from '@angular/router';
import { OAuthAuthService } from './oauth-auth.service';

/**
 * Guard para proteger rutas que requieren autenticación OAuth
 * Redirige a Cognito Hosted UI si el usuario no está autenticado
 */
@Injectable({
  providedIn: 'root',
})
export class OAuthAuthGuard implements CanActivate, CanActivateChild, CanMatch {
  private readonly oauthAuthService = inject(OAuthAuthService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.checkAuth(state.url);
  }

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.canActivate(childRoute, state);
  }

  canMatch(_route: Route, segments: UrlSegment[]): boolean {
    const url = segments.map((segment) => `/${segment.path}`).join('');
    return this.checkAuth(url);
  }

  private checkAuth(url: string): boolean {
    console.group('🛡️ [OAuth Guard] Verificando acceso a:', url);
    try {
      const isAuth = this.oauthAuthService.isAuthenticated();
      console.log('🔐 Usuario autenticado:', isAuth);

      if (isAuth) {
        console.log('✅ Acceso permitido');
        console.groupEnd();
        return true;
      }

      console.log('🚫 Usuario no autenticado, iniciando flujo de login...');
      // Si no está autenticado, iniciar flujo de login con la URL de destino
      this.oauthAuthService.login(url);
      console.groupEnd();
      return false;
    } catch (error) {
      console.error('❌ Error en OAuthAuthGuard:', error);
      console.error('📄 Detalles:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      });
      // En caso de error, permitir acceso para evitar bloqueos
      console.groupEnd();
      return true;
    }
  }
}

/**
 * Functional guard para usar con la nueva sintaxis de Angular
 */
export const oauthAuthGuard: CanActivateFn = (route, state) => {
  const oauthAuthService = inject(OAuthAuthService);

  console.group('🛡️ [OAuth Guard Functional] Verificando acceso a:', state.url);
  try {
    const isAuth = oauthAuthService.isAuthenticated();
    console.log('🔐 Usuario autenticado:', isAuth);

    if (isAuth) {
      console.log('✅ Acceso permitido');
      console.groupEnd();
      return true;
    }

    console.log('🚫 Usuario no autenticado, iniciando flujo de login...');
    // Iniciar flujo de login con la URL de destino
    oauthAuthService.login(state.url);
    console.groupEnd();
    return false;
  } catch (error) {
    console.error('❌ Error en oauthAuthGuard:', error);
    console.error('📄 Detalles:', {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    // En caso de error, permitir acceso para evitar bloqueos
    console.groupEnd();
    return true;
  }
};

/**
 * Guard para rutas que NO requieren autenticación (ej: login, callback)
 * Redirige a home si el usuario ya está autenticado
 */
@Injectable({
  providedIn: 'root',
})
export class OAuthNonAuthGuard implements CanActivate {
  private readonly oauthAuthService = inject(OAuthAuthService);
  private readonly router = inject(Router);

  canActivate(_route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): boolean {
    if (!this.oauthAuthService.isAuthenticated()) {
      return true;
    }

    // Si ya está autenticado, redirigir a dashboard
    this.router.navigate(['/dashboard']).then((r) => r);
    return false;
  }
}

/**
 * Functional guard para rutas no autenticadas
 */
export const oauthNonAuthGuard: CanActivateFn = () => {
  const oauthAuthService = inject(OAuthAuthService);
  const router = inject(Router);

  if (!oauthAuthService.isAuthenticated()) {
    return true;
  }

  router.navigate(['/dashboard']).then((r) => r);
  return false;
};
